/**
 * Theme lexicon - the user-facing language + imagery for each product skin.
 *
 * IMPORTANT: this swaps DISPLAY ONLY. Canonical keys (CommandKey), routes,
 * RPC names, edge-function names, and DB columns never change - they stay on
 * the original norse identifiers. A theme only re-labels what the operator
 * reads and re-paints the palette (via data-theme in globals.css).
 *
 *   norse    - BERSERKR. Viking war-saga. Iron, ember, shield walls, ravens.
 *   frontier - LAW DOGG. Doc-Holliday high-plains saga of justice. Leather,
 *              rust, wagon circles, bloodhounds, the bluff.
 */

/** Canonical command identifiers. Stable across themes - used in routes/state. */
export type CommandKey =
  | "raven"
  | "hounds"
  | "horn"
  | "shield-wall"
  | "arrows"
  | "oathbreaker"
  | "berserkrs";

export type ThemeId = "norse" | "frontier";

/** localStorage key - must match the boot script in index.html. */
export const THEME_STORAGE_KEY = "ldb-theme";

export interface CommandLex {
  /** Action label on the command button, e.g. "Call the Raven". */
  verb: string;
  /** Sub-label under the verb, e.g. "recon dossier". */
  description: string;
  /** Short feature noun, e.g. "Raven" / "Scout". For page headers. */
  noun: string;
  /** In-page primary action, lower-case, e.g. "sound the horn". */
  action?: string;
  /** Progress label while the action runs, e.g. "sounding...". */
  pending?: string;
  /** "last X" status verb, e.g. "last sounded" / "last cut". */
  lastLabel?: string;
}

/** Cross-cutting UI phrases that carry theme flavor. */
export interface UiLex {
  homeAria: string;
  corpusMaw: string;
  sagaTab: string;
  webTab: string;
  timelineWeaving: string;
  timelineRewoven: string;
  houndsReport: string;
  houndsNotFound: string;
  houndsOnTrail: string;
  arrowsNocking: string;
  ravenOpen: string;
  ravenDispatch: string;
  ravenFlying: string;
  ravenIdentifyHint: string;
  shieldWallEmpty: string;
  viewInShieldWall: string;
}

export interface ThemeLex {
  id: ThemeId;
  label: string;
  productName: string;
  surfaceName: string;
  throneLine: string;
  commandsAvailable: string;
  intakeLabel: string;
  intakeCta: string;
  greetingPre: string;
  greetingPost: string;
  footerLine: string;
  commands: Record<CommandKey, CommandLex>;
  ui: UiLex;
  backgroundVariant: string;
}

const NORSE: ThemeLex = {
  id: "norse",
  label: "Norse",
  productName: "BERSERKR",
  surfaceName: "War Room",
  throneLine: "command // issue from the throne",
  commandsAvailable: "7 available",
  intakeLabel: "// intake",
  intakeCta: "+ Feed the Eater a filing →",
  greetingPre: "The arena waits, ",
  greetingPost: ".",
  footerLine: "enter prepared. the arena does not wait.",
  backgroundVariant: "modes",
  commands: {
    raven: { verb: "Call the Raven", description: "recon dossier", noun: "Raven" },
    hounds: { verb: "Release the Hounds", description: "engines on evidence", noun: "Hounds" },
    horn: { verb: "Sound the Horn", description: "case timeline", noun: "Horn", action: "sound the horn", pending: "sounding…", lastLabel: "last sounded" },
    "shield-wall": { verb: "Form the Shield Wall", description: "defensive strategy", noun: "Shield Wall", action: "build the shield wall", pending: "raising the wall…" },
    arrows: { verb: "Light the Arrows", description: "burn the claim, point by point", noun: "Arrows", action: "muster the archers", pending: "loosing the volley…", lastLabel: "last loosed" },
    oathbreaker: { verb: "Name the Oathbreakers", description: "perjury & Rule 11 contradictions", noun: "Oathbreakers", action: "name the oathbreakers", pending: "weighing the oaths…" },
    berserkrs: { verb: "Send in the Berserkrs", description: "strike artifacts", noun: "Berserkrs" },
  },
  ui: {
    homeAria: "Return to the throne (war room)",
    corpusMaw: "eater",
    sagaTab: "the saga",
    webTab: "web of wyrd",
    timelineWeaving: "weaving the saga…",
    timelineRewoven: "timeline rewoven",
    houndsReport: "the hounds report",
    houndsNotFound: "not found in the corpus",
    houndsOnTrail: "hounds on the trail",
    arrowsNocking: "nocking arrows…",
    ravenOpen: "+ dispatch raven",
    ravenDispatch: "Dispatch raven →",
    ravenFlying: "flying...",
    ravenIdentifyHint: "(leave blank — Raven will identify from the case docket)",
    shieldWallEmpty: "no authorities extracted yet — build the shield wall",
    viewInShieldWall: "view in the shield wall",
  },
};

const FRONTIER: ThemeLex = {
  id: "frontier",
  label: "Frontier",
  productName: "LAW DOGG",
  surfaceName: "Marshal's Office",
  throneLine: "command // call it from the marshal's desk",
  commandsAvailable: "7 ready",
  intakeLabel: "// intake",
  intakeCta: "+ Sic the dog on a filing →",
  greetingPre: "The territory's waiting, ",
  greetingPost: ".",
  footerLine: "ride in ready. the territory don't wait.",
  backgroundVariant: "modes",
  commands: {
    raven: { verb: "Send the Scout", description: "recon dossier", noun: "Scout" },
    hounds: { verb: "Loose the Bloodhounds", description: "trackers on evidence", noun: "Bloodhounds" },
    horn: { verb: "Cut the Trail", description: "case timeline", noun: "The Trail", action: "cut the trail", pending: "cutting sign…", lastLabel: "last cut" },
    "shield-wall": { verb: "Circle the Wagons", description: "defensive strategy", noun: "Wagon Circle", action: "build the wagon circle", pending: "circling up…" },
    arrows: { verb: "Fan the Hammer", description: "burn the claim, shot by shot", noun: "The Hammer", action: "rally the gunhands", pending: "fanning the hammer…", lastLabel: "last fired" },
    oathbreaker: { verb: "Call the Bluff", description: "perjury & Rule 11 contradictions", noun: "The Bluff", action: "call the bluff", pending: "calling the bluff…" },
    berserkrs: { verb: "Send in the Marshals", description: "strike artifacts", noun: "Marshals" },
  },
  ui: {
    homeAria: "Return to the marshal's office (home)",
    corpusMaw: "kennel",
    sagaTab: "the trail",
    webTab: "the tangle",
    timelineWeaving: "cutting the trail…",
    timelineRewoven: "trail recut",
    houndsReport: "the bloodhounds report",
    houndsNotFound: "cold trail — nothing in the corpus",
    houndsOnTrail: "bloodhounds on the trail",
    arrowsNocking: "thumbing the hammers…",
    ravenOpen: "+ dispatch the scout",
    ravenDispatch: "Send the scout →",
    ravenFlying: "riding out...",
    ravenIdentifyHint: "(leave blank — the Scout will identify from the case docket)",
    shieldWallEmpty: "no authorities extracted yet — circle the wagons",
    viewInShieldWall: "view in the wagon circle",
  },
};

export const THEMES: Record<ThemeId, ThemeLex> = {
  norse: NORSE,
  frontier: FRONTIER,
};

export const DEFAULT_THEME: ThemeId = "frontier";

/** Coerce an arbitrary string into a valid ThemeId. */
export function normalizeThemeId(value: string | null | undefined): ThemeId {
  return value === "norse" ? "norse" : "frontier";
}
